// src/pages/.../lessonPlan/ClassWiseApprovalList.js
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { useSelector } from "react-redux";
import styles from "../../../../../../src/pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import CustomMRTTable from "../../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../../services/Api";

const renderCellWithDash = ({ cell }) => {
  const value = cell.getValue();
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

function ClassWiseApprovalList() {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const roleId = Number(user?.role?.id);
  const hideActions = [4, 6].includes(roleId);

  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || 3;
  const academic_id = clientData?.academic_year_id || "";

  // ----------------------------------------------------------------
  // State
  // ----------------------------------------------------------------
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Helper to read a single filter value from the columnFilters array
  const getFilterValue = useCallback(
    (id) => {
      const f = columnFilters.find((x) => x.id === id);
      const v = f?.value;
      return v === undefined || v === null ? "" : v;
    },
    [columnFilters],
  );

  // ----------------------------------------------------------------
  // API call — fetch class-wise lesson plan list
  // ----------------------------------------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        school_id,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        class_name: String(getFilterValue("class_code") || ""),
        section: String(getFilterValue("section") || ""),
        medium: String(getFilterValue("medium") || ""),
        academic_id: academic_id,
      };

      const response = await AxiosInstance.post(
        `/book/Hm-lesson-plans/classWiseList`,
        payload,
      );

      if (response?.data?.status) {
        const rawList = response.data.data || [];

        // Deduplicate by Class + Section + Medium — one row per unique combo.
        // IMPORTANT: preserve class_id and section_id (numeric ids) so we can
        // pass them to the view page; without them the view page's APIs fail
        // with 400 Bad Request.
        const seen = new Map();
        rawList.forEach((item) => {
          const class_code = item.class_name || item.class || "";
          const section = item.section_ids || item.section || "";
          const medium = item.medium || "";
          const key = `${class_code}||${section}||${medium}`;

          if (!seen.has(key)) {
            seen.set(key, {
              id: item.id || item.def_id,
              def_id: item.def_id,
              class_code,
              section,
              medium,
              // Preserve the numeric IDs that the view page needs.
              class_id: item.class_id ?? item.classId ?? null,
              section_id:
                item.section_id ?? item.sectionId ?? item.section_ids ?? null,
              lesson_plan_count: 1,
            });
          } else {
            seen.get(key).lesson_plan_count += 1;
          }
        });

        const deduped = Array.from(seen.values()).map((row, idx) => ({
          ...row,
          s_no: idx + 1,
        }));

        setData(deduped);
        setRowCount(response.data.pagination?.total || deduped.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (e) {
      console.error("Class-wise lesson plan fetch error:", e);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [school_id, pagination.pageIndex, pagination.pageSize, getFilterValue]);

  // Reset to page 0 whenever filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [columnFilters]);

  // Fetch on every relevant change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleColumnFiltersChange = (updaterOrValue) => {
    setColumnFilters((prev) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(prev)
        : updaterOrValue,
    );
  };

  // ----------------------------------------------------------------
  // Handlers
  // ----------------------------------------------------------------
  const handleView = useCallback(
    (row) => {
      navigate("/user/classWiseview", {
        state: {
          ID: row.id,
          data: row,
          // Numeric ids required by the view page APIs
          class_id: row.class_id,
          section_id: row.section,
          // Display-only fields
          class_code: row.class_code,
          section: row.section,
          medium: row.medium,
        },
      });
    },
    [navigate],
  );

  // ----------------------------------------------------------------
  // Columns: Class, Section, Medium, Action
  // ----------------------------------------------------------------
  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: "class_code",
        header: translations?.labels?.class || "Class",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: translations?.labels?.class || "Class",
        },
        Cell: renderCellWithDash,
      },
      {
        accessorKey: "section",
        header: translations?.labels?.section || "Section",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: translations?.labels?.section || "Section",
        },
        Cell: renderCellWithDash,
      },
      {
        accessorKey: "medium",
        header: translations?.labels?.medium || "Medium",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: translations?.labels?.medium || "Medium",
        },
        Cell: renderCellWithDash,
      },
    ];

    if (!hideActions) {
      cols.push({
        id: "actions",
        header: translations?.labels?.action || "Action",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={translations?.labels?.view || "View"}
          >
            <FaEye />
          </button>
        ),
      });
    }

    return cols;
  }, [translations, hideActions, handleView]);

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <CustomMRTTable
      columns={columns}
      data={data}
      loading={loading}
      pagination={pagination}
      rowCount={rowCount}
      onPaginationChange={setPagination}
      columnFilters={columnFilters}
      onColumnFiltersChange={handleColumnFiltersChange}
      enableColumnFilters={true}
      enableGlobalFilter={false}
      hideGlobalSearch={true}
      enableExpanding={false}
    />
  );
}

export default ClassWiseApprovalList;
